import { magnitude } from "../dynamics/vector";
import type { SubmitInputAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { StoneFlickingCoreConfig } from "../types/game-config";
import type { StoneFlickingCoreState } from "../types/game-state";
import { getEntityById } from "../utils/entity-helpers";
import {
  isParticipantActive,
  isParticipantKnown,
} from "../utils/participant-helpers";

export function validateSubmitInput(
  state: StoneFlickingCoreState,
  action: SubmitInputAction,
  config: StoneFlickingCoreConfig,
): ValidationResult {
  if (state.phase !== "INPUT_PHASE") {
    return { valid: false, error: "Not in input phase" };
  }

  if (!isParticipantKnown(state.participants, action.participantId)) {
    return { valid: false, error: "Unknown participant" };
  }

  if (
    !isParticipantActive(
      state.participants,
      state.eliminatedParticipants,
      action.participantId,
    )
  ) {
    return { valid: false, error: "Participant is eliminated" };
  }

  if (state.submittedParticipants.includes(action.participantId)) {
    return { valid: false, error: "Already submitted input this round" };
  }

  const entity = getEntityById(state.entities, action.entityId);
  if (!entity) {
    return { valid: false, error: "Entity not found" };
  }

  if (entity.eliminated) {
    return { valid: false, error: "Entity is eliminated" };
  }

  if (entity.owner !== action.participantId) {
    return {
      valid: false,
      error: "Cannot control another participant's entity",
    };
  }

  if (
    !Number.isFinite(action.impulse.x) ||
    !Number.isFinite(action.impulse.y)
  ) {
    return { valid: false, error: "Invalid impulse values" };
  }

  const impulseMagnitude = magnitude(action.impulse);
  if (impulseMagnitude < config.dynamics.minImpulseMagnitude) {
    return {
      valid: false,
      error: "Impulse too weak",
      details: {
        magnitude: impulseMagnitude,
        minimum: config.dynamics.minImpulseMagnitude,
      },
    };
  }

  const maxImpulse = config.dynamics.minImpulseMagnitude * 100;
  if (impulseMagnitude > maxImpulse) {
    return {
      valid: false,
      error: "Impulse too strong",
      details: {
        magnitude: impulseMagnitude,
        maximum: maxImpulse,
      },
    };
  }

  return { valid: true };
}
